import {
	object, string,
} from 'yup';
import dayjs from 'dayjs';

const mediaValidator = () => object().shape({
	id:             string().required("Please check media, it's missing it's param id"),
	mediaThumbnail: string(),
	mediaUrl:       string(),
	name:           string().required("Please check media, it's missing it's param name"),
});

const creativeTypeValidator = () => ({
	adCompanionAsset: mediaValidator(),
	adLogoAsset:      mediaValidator(),
	adAsset:          mediaValidator(),
	adCanvasAsset:    mediaValidator().notRequired().nullable(),
});

export const validateSpotifyAd = (data: Record<string, any>) => object().shape({
	adAdvertiserName: string()
		.required('Advertiser name is required')
		.matches(/^\S.*\S$/, 'Please remove any spaces from the beginning or end of the text.')
		.min(2, 'Advertiser name must be at least 2 characters')
		.max(25, 'Advertiser name must not be longer than 25 characters'),
	adName: string()
		.required('Ad name is required')
		.matches(/^\S.*\S$/, 'Please remove any spaces from the beginning or end of the text.')
		.min(3, 'Ad name must be at least 3 characters')
		.max(200, 'Ad name must not be longer than 200 characters'),
	adTagline: string()
		.min(2, 'Tagline must be at least 2 characters')
		.max(40, 'Tagline must not be longer than 40 characters')
		.matches(/^\S.*\S$/, 'Please remove any spaces from the beginning or end of the text.'),
	adCallToActionLanguage: string()
		.required('Ad language is required'),
	adStartTime: string()
		.notRequired()
		.test(
			'start-within-group-bounds',
			'Ad start time must be within group time boundaries',
			function (value) {
				const { groupStartTime, groupEndTime } = this.parent.parent;
				if (!value || !groupStartTime) return true;
				if (!dayjs(groupStartTime, 'YYYY-MM-DD HH:mm:ss', true).isValid()) return true;
				const start = dayjs(groupStartTime, 'YYYY-MM-DD HH:mm:ss', true);
				const adStart = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
				if (!adStart.isValid() || !start.isValid()) return true;

				// Ad start time must be at least 1 second after group start time
				const startPlusSecond = start.add(1, 'second');

				if (adStart.isBefore(startPlusSecond)) {
					return this.createError({ message: 'Ad start time must be at least 1 second after group start time' });
				}

				// Ad start time must be equal to or before group end time
				if (groupEndTime && dayjs(groupEndTime, 'YYYY-MM-DD HH:mm:ss', true).isValid()) {
					const end = dayjs(groupEndTime, 'YYYY-MM-DD HH:mm:ss', true);

					if (adStart.isAfter(end)) {
						return this.createError({ message: 'Ad start time must be equal to or before group end time' });
					}
				}

				return true;
			},
		),
	adEndTime: string()
		.notRequired()
		.test(
			'datetime-format',
			'Ad end time must be in format YYYY-MM-DD HH:mm:ss',
			value => !value || dayjs(value, 'YYYY-MM-DD HH:mm:ss', true).isValid(),
		)
		.test(
			'end-after-start',
			'Ad end time must be after start time',
			function (value) {
				const { adStartTime } = this.parent;
				if (!value || !adStartTime) return true;
				if (!dayjs(adStartTime, 'YYYY-MM-DD HH:mm:ss', true).isValid()) return true;
				const start = dayjs(adStartTime, 'YYYY-MM-DD HH:mm:ss', true);
				const end = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
				if (!end.isValid() || !start.isValid()) return true;
				if (!end.isAfter(start)) return this.createError({ message: 'End time must be after start time' });

				return true;
			},
		)
		.test(
			'end-within-group-bounds',
			'Ad end time must be within group time boundaries',
			function (value) {
				const { groupEndTime } = this.parent.parent;
				if (!value || !groupEndTime) return true;
				if (!dayjs(groupEndTime, 'YYYY-MM-DD HH:mm:ss', true).isValid()) return true;
				const end = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
				const groupEnd = dayjs(groupEndTime, 'YYYY-MM-DD HH:mm:ss', true);
				if (!end.isValid() || !groupEnd.isValid()) return true;

				// Ad end time must be equal to or before group end time
				if (end.isAfter(groupEnd)) {
					return this.createError({ message: 'Ad end time must be equal to or before group end time' });
				}

				return true;
			},
		),
	adCallToActionKey: string()
		.required('Call to action is required'),
	adCallToActionClickthroughUrl: string()
		.url('Clickthrough URL must be a valid URL')
		.required('Clickthrough URL is required'),
	adThirdPartyTrackingUrl: data.adThirdPartyTrackingMeasurementPartner
		? string().url('Must be a valid URL').required('Third-party tracking URL is required')
		: string().notRequired(),
	...(creativeTypeValidator()),
}).validate(data, { abortEarly: false });
